import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, ScanLine, SquareDashedMousePointer, Eye, Loader2, CheckCircle2, RefreshCw, Ruler } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { API, api, errMsg } from "./api";
import { useCatalog } from "./catalog";
import ZoneCanvas from "./ZoneCanvas";
import MaterialInspector from "./MaterialInspector";
import AISuggestions from "./AISuggestions";
import BwayCompare from "./BwayCompare";
import { useNavigate } from "react-router-dom";

const UploadBox = ({ onFile, uploading }) => (
  <label data-testid="elevation-upload-dropzone" className="flex min-h-[420px] cursor-pointer flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-zinc-800 bg-[#121215] p-10 text-center transition-colors duration-200 hover:border-sky-500/50">
    {uploading ? <Loader2 className="h-8 w-8 animate-spin text-sky-400" /> : <Upload className="h-8 w-8 text-sky-400" />}
    <div>
      <div className="font-[Manrope] text-lg font-semibold">Upload elevation drawing</div>
      <p className="mt-1 text-sm text-zinc-500">PDF (first page), PNG, JPG or WEBP · up to 25 MB</p>
    </div>
    <input data-testid="elevation-file-input" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" disabled={uploading} onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
  </label>
);

const MaterialMix = ({ zones, materials }) => {
  const total = zones.reduce((s, z) => s + z.w * z.h, 0) || 1;
  const rows = materials.map((m) => ({ m, share: zones.filter((z) => z.material_id === m.id).reduce((s, z) => s + z.w * z.h, 0) / total })).filter((r) => r.share > 0);
  if (!rows.length) return null;
  return (
    <div className="rounded-lg border border-zinc-800 bg-[#121215] p-5" data-testid="material-mix">
      <div className="mb-3 font-mono text-[11px] uppercase tracking-wider text-zinc-500">Material mix (by zone area)</div>
      <div className="mb-3 flex h-2.5 overflow-hidden rounded-full">{rows.map(({ m, share }) => <div key={m.id} style={{ width: `${share * 100}%`, background: m.color }} />)}</div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">{rows.map(({ m, share }) => <span key={m.id} className="font-mono text-xs text-zinc-300">{m.short} {(share * 100).toFixed(0)}%</span>)}</div>
    </div>
  );
};

const clampZone = (z) => {
  const x = Math.min(Math.max(0, z.x || 0), 99), y = Math.min(Math.max(0, z.y || 0), 99);
  return { ...z, x, y, w: Math.min(Math.max(1, z.w || 1), 100 - x), h: Math.min(Math.max(1, z.h || 1), 100 - y) };
};

const ScaleDialog = ({ open, onCancel, onSave }) => {
  const [m, setM] = useState("");
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-sm border-zinc-800 bg-[#121215]" data-testid="scale-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Manrope]">Real distance</DialogTitle>
          <DialogDescription>Enter the real length between the two points you picked.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input data-testid="scale-distance-input" type="number" step="0.01" min="0" value={m} onChange={(e) => setM(e.target.value)} placeholder="e.g. 12.0" className="border-zinc-800 bg-zinc-900 font-mono" />
          <span className="self-center font-mono text-sm text-zinc-400">m</span>
        </div>
        <Button data-testid="scale-save-btn" disabled={!(Number(m) > 0)} onClick={() => onSave(Number(m))} className="bg-sky-500 text-white hover:bg-sky-600">Set scale</Button>
      </DialogContent>
    </Dialog>
  );
};

export default function FacadeStudio({ project, setProject, onOpenDesign }) {
  const catalog = useCatalog();
  const materials = catalog?.materials || [];
  const [zones, setZones] = useState(project.zones || []);
  const [compareZone, setCompareZone] = useState(null);
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [drawMode, setDrawMode] = useState(false);
  const [rendered, setRendered] = useState(false);
  const [busy, setBusy] = useState(null);
  const [saveState, setSaveState] = useState("saved");
  const timer = useRef(null);
  const [scaleMode, setScaleMode] = useState(false);
  const [scalePts, setScalePts] = useState([]);
  const el = project.elevation;

  const persist = (next) => {
    setZones(next);
    setProject((p) => ({ ...p, zones: next }));
    setSaveState("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        await api.put(`/projects/${project.id}/zones`, { zones: next });
        setSaveState("saved");
      } catch (e) {
        setSaveState("error");
        toast.error(errMsg(e, "Could not save zones"));
      }
    }, 600);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  const upload = async (file) => {
    setBusy("upload");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/projects/${project.id}/elevation`, fd);
      setProject((p) => ({ ...p, elevation: data, zones: [], ai_suggestions: null, zone_summary: null }));
      setZones([]);
      toast.success("Elevation uploaded. Run AI detection or draw zones yourself.");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setBusy(null);
    }
  };

  const detect = async () => {
    setBusy("detect");
    try {
      const { data } = await api.post(`/projects/${project.id}/detect-zones`);
      setZones(data.zones);
      setProject((p) => ({ ...p, zones: data.zones, zone_summary: { building_summary: data.building_summary, floors_estimated: data.floors_estimated } }));
      toast.success(`${data.zones.length} zones detected`);
    } catch (e) {
      toast.error(errMsg(e, "Detection failed"));
      const { data: fresh } = await api.get(`/projects/${project.id}`).catch(() => ({ data: null }));
      if (fresh?.zones?.length) { setZones(fresh.zones); setProject((p) => ({ ...p, zones: fresh.zones, zone_summary: fresh.zone_summary })); }
    } finally {
      setBusy(null);
    }
  };

  const addZone = (box) => {
    const z = { id: Math.random().toString(36).slice(2, 10), label: `Zone ${zones.length + 1}`, ...box, material_id: null, notes: "", source: "manual" };
    persist([...zones, z]);
    setSelected(z.id);
    setDrawMode(false);
  };

  const applyMapping = (mapping, finishes = {}) => {
    persist(zones.map((z) => {
      const m = materials.find((x) => x.id === mapping[z.id]);
      if (!m) return z;
      const aiFinish = finishes[z.id];
      const finish = m.finishes.find((f) => aiFinish && aiFinish.toLowerCase().includes(f.split(" ")[0].toLowerCase())) || m.finishes[0];
      const notes = aiFinish && !(z.notes || "").includes(aiFinish) ? `${z.notes ? z.notes + "\n" : ""}AI finish: ${aiFinish}` : z.notes;
      return { ...z, material_id: m.id, thickness_mm: m.default_thickness, finish, notes };
    }));
    setRendered(true);
    toast.success("Materials applied");
  };

  const pickPoint = (p) => {
    const pts = scalePts.length >= 2 ? [p] : [...scalePts, p];
    setScalePts(pts);
  };
  const saveScale = async (distance_m) => {
    try {
      const { data } = await api.put(`/projects/${project.id}/scale`, { p1: scalePts[0], p2: scalePts[1], distance_m });
      setProject((p) => ({ ...p, scale: data }));
      toast.success("Drawing scale set");
    } catch (e) {
      toast.error(errMsg(e, "Could not set scale"));
    }
    setScaleMode(false);
    setScalePts([]);
  };
  const zoneSize = (z) => (project.scale && el ? { w: (z.w / 100) * el.width * project.scale.m_per_px, h: (z.h / 100) * el.height * project.scale.m_per_px } : null);
  const createSystem = async (zone, system_type, brand = "bhoruka") => {
    clearTimeout(timer.current);
    try {
      await api.put(`/projects/${project.id}/zones`, { zones });
      const { data } = await api.post(`/projects/${project.id}/zones/${zone.id}/design`, { system_type, brand });
      toast.success(`${data.name} sized at ${zoneSize(zone).w.toFixed(2)} × ${zoneSize(zone).h.toFixed(2)} m`);
      if (brand === "bway") navigate(`/bway?project=${project.id}&design=${data.id}`);
      else onOpenDesign(data);
    } catch (e) {
      toast.error(errMsg(e, "Could not create system"));
    }
  };

  const openCompare = async (z) => {
    clearTimeout(timer.current);
    try {
      await api.put(`/projects/${project.id}/zones`, { zones });
      setCompareZone(z);
    } catch (e) {
      toast.error(errMsg(e, "Could not save zones"));
    }
  };

  if (!el) return <UploadBox onFile={upload} uploading={busy === "upload"} />;
  const zone = zones.find((z) => z.id === selected);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3" data-testid="studio-toolbar">
        <Button data-testid="detect-zones-btn" onClick={detect} disabled={!!busy} className="bg-sky-500 text-white hover:bg-sky-600">
          {busy === "detect" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ScanLine className="mr-2 h-4 w-4" />}AI detect zones
        </Button>
        <Button data-testid="draw-zone-toggle" variant="outline" onClick={() => setDrawMode(!drawMode)} className={drawMode ? "border-amber-400 bg-amber-400/10 text-amber-300 hover:text-white" : "border-zinc-700 hover:text-white"}>
          <SquareDashedMousePointer className="mr-2 h-4 w-4" />{drawMode ? "Drag on drawing…" : "Draw zone"}
        </Button>
        <Button data-testid="set-scale-btn" variant="outline" onClick={() => { setScaleMode(!scaleMode); setScalePts([]); setDrawMode(false); }} className={scaleMode ? "border-amber-400 bg-amber-400/10 text-amber-300 hover:text-white" : "border-zinc-700 hover:text-white"}>
          <Ruler className="mr-2 h-4 w-4" />{scaleMode ? `Pick point ${Math.min(scalePts.length + 1, 2)} of 2` : "Set scale"}
        </Button>
        <span data-testid="scale-status" className="font-mono text-[11px] text-zinc-500">{project.scale ? `1 px = ${(project.scale.m_per_px * 1000).toFixed(1)} mm` : "Scale not set"}</span>
        <label className="flex items-center gap-2 rounded-md border border-zinc-800 px-3 py-2 text-sm text-zinc-300">
          <Eye className="h-4 w-4" />Render materials
          <Switch data-testid="render-materials-switch" checked={rendered} onCheckedChange={setRendered} />
        </label>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-zinc-800 px-3 py-2 text-sm text-zinc-400 hover:text-white">
          <RefreshCw className="h-4 w-4" />Replace drawing
          <input data-testid="replace-elevation-input" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
        </label>
        <span data-testid="zones-save-state" className="ml-auto inline-flex items-center gap-1.5 font-mono text-xs text-zinc-500">
          {saveState === "saving" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className={`h-3.5 w-3.5 ${saveState === "error" ? "text-red-400" : "text-emerald-400"}`} />}
          {saveState === "saving" ? "Saving" : saveState === "error" ? "Save failed" : `${zones.length} zones saved`}
        </span>
      </div>
      {project.zone_summary?.building_summary && <p className="text-sm text-zinc-400" data-testid="zone-summary">{project.zone_summary.building_summary}</p>}
      <div className="grid gap-6 xl:grid-cols-12">
        <div className="xl:col-span-8">
          <ZoneCanvas imageUrl={`${API}/projects/${project.id}/elevation/image?v=${encodeURIComponent(el.uploaded_at)}`} zones={zones} materials={materials}
            selectedId={selected} onSelect={setSelected} drawMode={drawMode} onDrawn={addZone} rendered={rendered}
            scaleMode={scaleMode} scalePts={scalePts} onPoint={pickPoint} />
          <ScaleDialog open={scaleMode && scalePts.length === 2} onCancel={() => { setScalePts([]); }} onSave={saveScale} />
          <p className="mt-2 font-mono text-[11px] text-zinc-600">{el.filename} · {el.width}×{el.height}px</p>
        </div>
        <div className="space-y-4 xl:col-span-4">
          <MaterialInspector zone={zone} materials={materials} size={zone ? zoneSize(zone) : null} onCreateSystem={createSystem} onCompare={openCompare} onChange={(z) => persist(zones.map((x) => (x.id === z.id ? clampZone(z) : x)))} onDelete={(id) => { persist(zones.filter((x) => x.id !== id)); setSelected(null); }} />
          <div className="rounded-lg border border-zinc-800 bg-[#121215] p-3" data-testid="zone-list">
            {zones.length === 0 && <p className="p-2 text-sm text-zinc-500">No zones yet.</p>}
            {zones.map((z) => {
              const m = materials.find((x) => x.id === z.material_id);
              return (
                <button key={z.id} data-testid={`zone-list-item-${z.id}`} onClick={() => setSelected(z.id)} className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors duration-150 ${z.id === selected ? "bg-sky-500/10 text-white" : "text-zinc-400 hover:bg-zinc-800/60 hover:text-white"}`}>
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: m?.color || "#52525b" }} />
                  <span className="flex-1 truncate">{z.label}</span>
                  <span className="font-mono text-[10px] text-zinc-500">{m?.short || "unassigned"}</span>
                </button>
              );
            })}
          </div>
          <MaterialMix zones={zones} materials={materials} />
        </div>
      </div>
      <BwayCompare project={project} zone={compareZone} open={!!compareZone} onClose={() => setCompareZone(null)} onSaved={onOpenDesign} />
      <AISuggestions projectId={project.id} suggestions={project.ai_suggestions} setSuggestions={(s) => setProject((p) => ({ ...p, ai_suggestions: s }))} zones={zones} materials={materials} onApply={applyMapping} />
    </div>
  );
}
